import type { ReactNode } from "react";
import { useOnboarding } from "@boundary/_hooks/useOnboarding";

export interface OnboardingElementProps<
  TData extends object = {},
  T extends string = string
> {
  data: TData;
  onComplete: (nextStep: T | null, data?: Partial<TData>) => Promise<void>;
  disabled?: boolean;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

export type OnboardingElement<
  TData extends object = {},
  T extends string = string
> = (props: OnboardingElementProps<TData, T>) => ReactNode;

export type CompleteAction<
  TData extends object = {},
  T extends string = string
> = (nextSlide: T | null, data: TData) => Promise<TData | void>;

export interface OnboardingSlideMetadata {
  id?: string;
  title?: string;
  description?: string;
  duration?: number;
  allowSwipe?: boolean;
  allowSkip?: boolean;
  [key: string]: unknown;
}

export interface OnboardingSlide<
  TData extends object = {},
  T extends string = string
> {
  step: T;
  element: OnboardingElement<TData, T>;
  metadata?: OnboardingSlideMetadata;
}

interface OnboardingProps<TData extends object, T extends string> {
  slides: OnboardingSlide<TData, T>[];
  startingSlide: T;
  onComplete: (data: TData) => void | Promise<void>;
  initialData?: Partial<TData>;
  enableSwipe?: boolean;
  enableAutoAdvance?: boolean;
}

function useSwipeHandlers<TData extends object, T extends string>(
  enableSwipe: boolean,
  currentStep: OnboardingSlide<TData, T> | undefined,
  goNext: () => void,
  goPrev: () => void
) {
  const handleSwipeLeft = () => {
    if (enableSwipe && currentStep?.metadata?.allowSwipe) {
      goNext();
    }
  };

  const handleSwipeRight = () => {
    if (enableSwipe && currentStep?.metadata?.allowSwipe) {
      goPrev();
    }
  };

  return {
    onSwipeLeft: enableSwipe ? handleSwipeLeft : undefined,
    onSwipeRight: enableSwipe ? handleSwipeRight : undefined,
  };
}

export function Onboarding<TData extends object, T extends string>({
  slides,
  startingSlide,
  onComplete,
  initialData,
  enableSwipe = false,
  enableAutoAdvance = false,
}: OnboardingProps<TData, T>) {
  const {
    currentStep,
    data,
    loading,
    onComplete: handleComplete,
    goNext,
    goPrev,
  } = useOnboarding({
    slides,
    startingSlide,
    onComplete,
    initialData,
  });

  const swipeHandlers = useSwipeHandlers(enableSwipe, currentStep, goNext, goPrev);

  if (!currentStep) {
    return null;
  }

  const CurrentStepElement = currentStep.element;

  return (
    <CurrentStepElement
      data={data}
      onComplete={handleComplete}
      disabled={loading}
      {...swipeHandlers}
    />
  );
}

